/**
 * Pipeline Integration - Integración del pipeline en el flujo de extracción
 * 
 * FMT-03: Conecta processingPipeline con documentExtractor para aplicar
 * filtros en orden óptimo según el formato detectado.
 * 
 * @module pipeline/integration
 */

import { processText, processBatch } from './processingPipeline';
import { detectFormat, type DocumentFormat, type FormatAnalysis } from '@/utils/formatDetector';

// ============================================================================
// TIPOS
// ============================================================================

/** Contexto de procesamiento para un documento */
export interface DocumentProcessingContext {
  /** Nombre del archivo */
  fileName: string;
  /** Formato detectado */
  format: FormatAnalysis;
  /** Municipio (opcional) */
  municipio?: string;
  /** Número de registros */
  recordCount: number;
}

/** Resultado del pre-procesamiento de texto */
export interface PreProcessedText {
  /** Texto limpio listo para extracción */
  cleaned: string;
  /** Texto original */
  original: string;
  /** Filtros aplicados */
  filtersApplied: string[];
  /** Número de modificaciones */
  modifications: number;
}

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Pre-procesa un valor de coordenada aplicando filtros del pipeline.
 * Usado antes de cleanCoordinateValue para limpiar artefactos de formato.
 */
export function preProcessCoordinateValue(
  value: string,
  format: DocumentFormat = 'plain'
): PreProcessedText {
  if (!value || typeof value !== 'string') {
    return { cleaned: '', original: '', filtersApplied: [], modifications: 0 };
  }
  
  const result = processText(value, { format });
  
  return {
    cleaned: result.text,
    original: value,
    filtersApplied: result.appliedFilters,
    modifications: Object.values(result.modifications).reduce((a, b) => a + b, 0),
  };
}

/**
 * Pre-procesa un valor de dirección aplicando filtros del pipeline.
 */
export function preProcessAddressValue(
  value: string,
  format: DocumentFormat = 'plain'
): PreProcessedText {
  if (!value || typeof value !== 'string') {
    return { cleaned: '', original: '', filtersApplied: [], modifications: 0 };
  }
  
  const result = processText(value, { format });
  
  return {
    cleaned: result.text,
    original: value,
    filtersApplied: result.appliedFilters,
    modifications: Object.values(result.modifications).reduce((a, b) => a + b, 0),
  };
}

/**
 * Pre-procesa múltiples valores en lote.
 * Más eficiente que llamar preProcessCoordinateValue múltiples veces.
 */
export function preProcessBatch(
  values: string[],
  format: DocumentFormat = 'plain'
): PreProcessedText[] {
  const result = processBatch(values, { format });
  
  return result.records.map(r => ({
    cleaned: r.processed,
    original: r.original,
    filtersApplied: r.filters,
    modifications: r.filters.length, // Aproximación
  }));
}

/**
 * Detecta y configura el contexto de procesamiento para un documento.
 */
export function createProcessingContext(
  fileName: string,
  sampleContent: string,
  recordCount: number,
  municipio?: string
): DocumentProcessingContext {
  const format = detectFormat(sampleContent, fileName);
  
  return {
    fileName,
    format,
    municipio,
    recordCount,
  };
}

/**
 * Procesa un registro completo (nombre, dirección, coordenadas).
 */
export function preProcessRecord(
  record: {
    nombre?: string;
    direccion?: string;
    xOriginal?: string;
    yOriginal?: string;
  },
  format: DocumentFormat = 'plain'
): {
  nombre: PreProcessedText;
  direccion: PreProcessedText;
  xOriginal: PreProcessedText;
  yOriginal: PreProcessedText;
  totalModifications: number;
} {
  const nombre = preProcessAddressValue(record.nombre || '', format);
  const direccion = preProcessAddressValue(record.direccion || '', format);
  const xOriginal = preProcessCoordinateValue(record.xOriginal || '', format);
  const yOriginal = preProcessCoordinateValue(record.yOriginal || '', format);
  
  return {
    nombre,
    direccion,
    xOriginal,
    yOriginal,
    totalModifications: 
      nombre.modifications + 
      direccion.modifications + 
      xOriginal.modifications + 
      yOriginal.modifications,
  };
}

/**
 * Estima el impacto del pipeline en un conjunto de datos.
 */
export function estimatePipelineImpact(
  sampleValues: string[],
  format: DocumentFormat
): {
  valuesModified: number;
  totalModifications: number;
  percentModified: number;
  dominantFilters: string[];
} {
  const results = preProcessBatch(sampleValues, format);
  
  const valuesModified = results.filter(r => r.modifications > 0).length;
  const totalModifications = results.reduce((sum, r) => sum + r.modifications, 0);
  
  // Contar frecuencia de filtros
  const filterCounts: Record<string, number> = {};
  for (const r of results) {
    for (const f of r.filtersApplied) {
      filterCounts[f] = (filterCounts[f] || 0) + 1;
    }
  }
  
  // Top 3 filtros más usados
  const dominantFilters = Object.entries(filterCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([filter]) => filter);
  
  return {
    valuesModified,
    totalModifications,
    percentModified: sampleValues.length > 0 
      ? Math.round((valuesModified / sampleValues.length) * 100) 
      : 0,
    dominantFilters,
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  preProcessCoordinateValue,
  preProcessAddressValue,
  preProcessBatch,
  preProcessRecord,
  createProcessingContext,
  estimatePipelineImpact,
};
